import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import gsap from 'gsap';
import 'datatables.net-dt/css/dataTables.dataTables.min.css';
import '../styles/manageCategory.css';
import '../styles/dataTable.css';
import '../styles/floatingForm.css';

import searchIcon from '../assets/images/search_icon.svg';
import addIcon from '../assets/images/add-icon.svg';
import arrowIcon from '../assets/images/Arrow.svg';
import upwardArrowIcon from '../assets/images/upward_arrow.svg';
import editIcon from '../assets/images/edit_icon.svg';

const ManageAirlines = () => {
  const [airlines, setAirlines] = useState([]);
  const [airlineName, setAirlineName] = useState('');
  const [logo_image_path, setlogo_image_path] = useState(null);
  const [airlineId, setAirlineId] = useState(null);
  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const modalRef = useRef(null);

  // Bulletproof fallback image (Transparent Pixel)
  const fallbackImage = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

  // Table states
  const [search, setSearch] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  const filteredData = airlines.filter((item) =>
    item.airline_name.toLowerCase().includes(search.toLowerCase())
  );

  if (sortField) {
    filteredData.sort((a, b) => {
      const valA = a[sortField] || "";
      const valB = b[sortField] || "";
      return sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    });
  }

  const totalPages = Math.ceil(filteredData.length / entriesPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(1);
  }, [search, entriesPerPage, totalPages]);

  const handleSort = (field) => {
    if (sortField === field) setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortOrder("asc"); }
  };

  useEffect(() => { fetchAirlines(); }, []);

  const fetchAirlines = async () => {
    try {
      const response = await api.get('/airlines/get-all');
      setAirlines(response.data.data);
    } catch (error) {
      console.error('Error fetching airlines:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let errors = {};
    if (!airlineName.trim()) errors.airlineName = "Airline Name cannot be empty";

    if (!logo_image_path) {
      errors.logo_image_path = "Logo Image cannot be empty";
    } else if (logo_image_path instanceof File) {
      const allowedImageTypes = ["image/png", "image/jpeg", "image/webp"];
      if (!allowedImageTypes.includes(logo_image_path.type)) {
        errors.logo_image_path = "Only PNG, JPG and WEBP allowed";
      }
    }

    if (Object.keys(errors).length > 0) { setErrors(errors); return; }
    setErrors({});

    const formData = new FormData();
    formData.append('airline_name', airlineName);
    
    if (logo_image_path instanceof File) {
      formData.append('logo_image_path', logo_image_path);
    }

    try {
      let response;
      if (airlineId) {
        response = await api.post(`/airlines/update/${airlineId}`, formData);
      } else {
        response = await api.post('/airlines/add', formData);
      }

      if (response.data.status === "success" || response.data.status === 1) {
        toast.success(airlineId ? "Airline updated!" : "Airline created!");
        handleCancel();
        fetchAirlines();
      } else {
        toast.error(response.data.message || "Operation failed");
      }
    } catch (error) {
      console.error('Error saving airline:', error);
      toast.error('Error saving airline');
    }
  };

  const handleEdit = (airline) => {
    setAirlineId(airline.id);
    setAirlineName(airline.airline_name);
    setlogo_image_path(airline.logo_image_path);
    setErrors({});
    setShowModal(true);
  };

  useEffect(() => {
    if (showModal && modalRef.current) {
      gsap.fromTo(
        modalRef.current,
        { autoAlpha: 0, y: 50, scale: 0.95 },
        { autoAlpha: 1, y: 0, scale: 1, duration: 0.5, ease: "power2.out" }
      );
    }
  }, [showModal]);

  const handleCancel = () => {
    if (modalRef.current) {
      gsap.to(modalRef.current, {
        autoAlpha: 0,
        y: 50,
        scale: 0.95,
        duration: 0.4,
        ease: "power2.in",
        onComplete: () => {
          setShowModal(false);
          setAirlineId(null);
          setAirlineName('');
          setlogo_image_path(null);
        },
      });
    } else setShowModal(false);
  };

  return (
    <div style={{ position: "relative" }}>
      <div style={{ padding: "20px", opacity: showModal ? 0.5 : 1, pointerEvents: showModal ? 'none' : 'auto' }}>
        <div className="table-header">
          <h2 style={{ fontWeight: 700 }}>Manage Airlines</h2>
          <div className="search-container">
            <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} placeholder="Search" />
            <img src={searchIcon} alt="search-icon" className="search-icon" />
          </div>
          <div className="entries-container">
            <label htmlFor="noOfEntries">Show: </label>
            <select value={entriesPerPage} id="noOfEntries" onChange={(e) => setEntriesPerPage(Number(e.target.value))}>
              <option value={10}>10 Entries per page</option>
              <option value={50}>50 Entries per page</option>
              <option value={100}>100 Entries per page</option>
            </select>
          </div>
          <div className="action-btn">
            <button onClick={() => { setAirlineId(null); setAirlineName(''); setlogo_image_path(null); setErrors({}); setShowModal(true); }}>
              <img src={addIcon} alt="add-icon" /> Add Airline
            </button>
          </div>
        </div>

        <table width="100%" cellPadding="10" className="table-no-outer-border">
          <thead>
            <tr>
              <th style={{width:"10%",textAlign:"center"}}>Sr. No.</th>
              <th onClick={() => handleSort("airline_name")} style={{cursor: 'pointer'}}>
                <div className="name-container" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  Airline Name
                  <div className="sort-arrow-container">
                    <img src={upwardArrowIcon} alt="up" style={{ opacity: sortField === "airline_name" && sortOrder === "asc" ? 1 : 0.5 }} />
                    <img src={upwardArrowIcon} alt="down" style={{ transform: "rotateX(180deg)", marginTop: "5px", opacity: sortField === "airline_name" && sortOrder === "desc" ? 1 : 0.5 }} />
                  </div>
                </div>
              </th>
              <th style={{width:"30%",textAlign:"center"}}>Logo Image</th>
              <th style={{width:"30%",textAlign:"center"}}>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? paginatedData.map((row, i) => {
              const srNo = (currentPage - 1) * entriesPerPage + i + 1;
              return (
                <tr key={row.id}>
                  <td style={{textAlign:"center"}}>{srNo}</td>
                  <td style={{textAlign:"center "}}>{row.airline_name}</td>
                  <td style={{ textAlign: "center" }}>
                    {/* ONLY SHOW IMAGE IF PATH IS PRESENT */}
                    {row.logo_company ? (
                      <img
                        key={row.id} 
                        style={{ width: '60px', height: '40px', objectFit: 'contain', borderRadius: '5px' }}
                        /* CACHE BUSTER: Added ?t=Date.now() to force refresh */
                        src={`${process.env.REACT_APP_IMAGE_BASE_URL}${row.logo_company}`}
                        alt="Logo"
                
                      />
                    ) : (
                      <span style={{ color: '#999', fontSize: '12px' }}>No Image</span>
                    )}
                  </td>
                  <td className="center middle" style={{ display: "flex", justifyContent: "center" }}>
                    <button className="edit-button" onClick={() => handleEdit(row)}>
                      <img src={editIcon} alt="editIcon" /> Edit
                    </button>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={4} style={{ textAlign: "center" }}>No data found</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination Logic... */}
        <div style={{ paddingBottom: '30px', background: '#fff' }}>
          <div style={{ padding: "30px", textAlign: "center", borderTop: '2px solid #F2F2F2', borderBottom: '2px solid #F2F2F2' }}>
            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} style={{margin: "0px 5px", background:"transparent", border: "none", cursor:" pointer"}}>
              <img src={arrowIcon} alt="prev" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button key={page} onClick={() => setCurrentPage(page)}
                style={{ margin: "0 5px", background: currentPage === page ? "#D9D9D9" : "#fff" ,border: "none", cursor: 'pointer', padding: '5px 10px'}}>{page}</button>
            ))}
            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages || totalPages === 0} style={{margin: "0px 5px", background:"transparent", border: "none", cursor:" pointer"}}>
              <img src={arrowIcon} alt="next" style={{ transform: 'rotateY(180deg)' }} />
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div ref={modalRef} className="floating-form-container smaller" style={{ zIndex: 1000 }}>
          <div className="heading">
            <h2>{airlineId ? "Edit Airline" : "Add Airline"}</h2>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-container" style={{padding:"20px"}}>
              <div className="input-container-floating-form wid-100">
                <label>Airline Name *</label>
                <input type="text" value={airlineName} onChange={e => setAirlineName(e.target.value)} />
                {errors.airlineName && <span className="text-danger small">{errors.airlineName}</span>}
              </div>

              <div className="input-container-floating-form wid-100">
                <label htmlFor="logo">Package Image *</label>
                <div className="file-select">
                  <div className="file-select-button">Choose File</div>
                  <div className="file-select-name">
                    {logo_image_path instanceof File ? logo_image_path.name : (logo_image_path ? "File selected" : "No file chosen...")}
                  </div>
                  <input
                    type="file"
                    id="logo"
                    accept="image/*"
                    onChange={(e) => setlogo_image_path(e.target.files[0])}
                  />
                </div>
                {errors.logo_image_path && <span className="text-danger small"><b>{errors.logo_image_path}</b></span>}
              </div>
            </div>
            <div className="form-btn-container">
              <button type="button" onClick={handleCancel}>Cancel</button>
              <button type="submit">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ManageAirlines;